import { useState, lazy, Suspense, useEffect } from 'react';
import { isLoggedIn, getFirstName } from '../utils/userUtils';
import { useLocation, Link } from 'react-router-dom';
import { useHydrated } from '../hooks/useHydrated';
const UserMenu = lazy(() => import('./UserMenu'));
import { PageTitle } from './common';

// Dynamically import the AuthModal component
const AuthModal = lazy(() => import('./auth/AuthModal'));

function Header() {
  const hydrated = useHydrated();
  const [isHomepage, setIsHomepage] = useState(false);
  // openAuthModal events are counted here, in the always-mounted Header, because the
  // lazy AuthModal may not have mounted yet when one fires (its Suspense boundary lets
  // the header commit first) - a listener inside the modal would miss those events.
  const [authModalOpenRequests, setAuthModalOpenRequests] = useState(0);

  const location = useLocation();

  useEffect(() => {
    const handleOpenAuthModal = () => setAuthModalOpenRequests((count) => count + 1);
    document.addEventListener('openAuthModal', handleOpenAuthModal);
    return () => document.removeEventListener('openAuthModal', handleOpenAuthModal);
  }, []);

  useEffect(() => {
    const checkIfHomepage = () => {
      const path = location.pathname;
      setIsHomepage(path === '/' || path === '');
    };
    
    checkIfHomepage();
  }, [location]);

  return (
    <header className="pb-4 pt-2" role="banner">
      <nav className="flex justify-between items-center" aria-label="Main Navigation">
        <div className='user-menu flex items-center gap-3'>
          {/* Gated on `hydrated`, not just called directly - isLoggedIn() reads
              document.cookie, which the SSR stub (src/ssr/stubs/userUtils.js) always
              answers as logged-out. A logged-in visitor's first client render would
              otherwise jump straight to UserMenu, mismatching the server-rendered
              logged-out button (React #418/#423) - see useHydrated.js. */}
          {hydrated && isLoggedIn() ? (
            <Suspense fallback={<div className="text-center py-4">שלום {getFirstName()}</div>}>
              <UserMenu />
            </Suspense>
          ) : (
            <>
              <button
                className="log-in-button px-4 py-1 text-sm font-semibold text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200 border border-gray-200"
                onClick={() => document.dispatchEvent(new CustomEvent('openAuthModal'))}
                aria-haspopup="dialog"
              >
                התחברו
              </button>
            </>
          )}
        </div>
        {!isHomepage && (
          /* Real anchor (not onClick) so the logo also works on the inert SSR search
             pages, where no client-side JS runs. Same full-page navigation as before. */
          <a href="/" aria-label="בוא תימצא">
            <PageTitle variant="logo" level="h3" id="logo" center showBeta>
              בואתִמצא
            </PageTitle>
          </a>
        )}
        {/* <div className="space-x-4">
          <a 
            href="#" 
            className="text-gray-600 hover:text-indigo-600"
            aria-label="אודות"
          >
            אודות
          </a>
        </div> */}
      </nav>

      {/* Suspense boundary is required: a lazy component with no boundary throws under
          SSR's renderToString (entry-server.jsx). Null fallback — the modal is invisible
          until the openAuthModal event fires, so there is nothing to show while loading.
          Gated on `hydrated` (not just the Suspense fallback) so the lazy import is never
          even triggered until after hydration commits — see useHydrated.js for why. */}
      {hydrated && (
        <Suspense fallback={null}>
          <AuthModal
            openRequests={authModalOpenRequests}
            onAuthSuccess={() => {
              // Refresh the page after successful login
              window.location.reload();
            }}
          />
        </Suspense>
      )}
    </header>
  );
}

export default Header;
